import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Mail, MapPin, User, CreditCard, CheckCircle, Loader2, Shield, Truck, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreOrderPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    product: '',
    quantity: '1',
    color: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.address || !form.city || !form.state || !form.zip || !form.product) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/preorder`, {
        ...form,
        quantity: parseInt(form.quantity),
        origin_url: window.location.origin
      });
      
      // If Stripe checkout URL is returned, redirect to it
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setOrderId(response.data.order_id);
        setOrderComplete(true);
        toast.success('Pre-order submitted successfully!');
      }
    } catch (error) {
      console.error('Pre-order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit pre-order. Please try again.');
      setIsLoading(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="preorder-page">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Pre-Order Received!
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Order ID: <strong className="text-accent">{orderId}</strong>
            </p>
            <Card className="bg-card border-accent/30 mb-8">
              <CardContent className="p-6 text-left">
                <h3 className="font-bold text-lg mb-4 text-accent">What Happens Next?</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-accent mt-0.5" />
                    <span>Confirmation email sent to <strong className="text-foreground">{form.email}</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CreditCard className="w-5 h-5 text-accent mt-0.5" />
                    <span>Payment instructions will be sent within <strong className="text-foreground">24 hours</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Truck className="w-5 h-5 text-accent mt-0.5" />
                    <span>Estimated shipping: <strong className="text-foreground">2-4 weeks</strong> after payment</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Link to="/">
              <Button className="btn-primary">Back to Home</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="preorder-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-accent" />
          </div>
          <h1 className="section-header mb-4">Pre-Order Form</h1>
          <p className="section-subheader max-w-xl mx-auto">
            Reserve your item today. Secure checkout with Stripe.
          </p>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-6 mb-12"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-5 h-5 text-green-500" />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Truck className="w-5 h-5 text-accent" />
            <span>Fast Shipping</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RotateCcw className="w-5 h-5 text-secondary" />
            <span>Easy Returns</span>
          </div>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit}>
            {/* Product Selection */}
            <Card className="bg-card border-accent/30 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-accent" />
                  Product Details
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="product">Product / Item Name *</Label>
                    <Input
                      id="product"
                      value={form.product}
                      onChange={(e) => setForm({ ...form, product: e.target.value })}
                      placeholder="Enter the product name you want to pre-order"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quantity">Quantity *</Label>
                      <Select value={form.quantity} onValueChange={(v) => setForm({ ...form, quantity: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((q) => (
                            <SelectItem key={q} value={q.toString()}>{q}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="color">Color / Variant (if applicable)</Label>
                      <Input
                        id="color"
                        value={form.color}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        placeholder="e.g., Blue, Large, etc."
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Contact Information
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  Shipping Address
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="123 Main St, Apt 4B"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        placeholder="OR"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input
                        id="zip"
                        value={form.zip}
                        onChange={(e) => setForm({ ...form, zip: e.target.value })}
                        placeholder="97701"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United States">United States</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                        <SelectItem value="Australia">Australia</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Special Instructions */}
            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <Label htmlFor="notes">Special Instructions or Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any special requests, gift messages, or additional information..."
                  rows={3}
                  className="mt-2"
                />
              </CardContent>
            </Card>

            {/* Policies */}
            <Card className="bg-muted/30 border-border/50 mb-6">
              <CardContent className="p-4">
                <h3 className="font-bold text-sm mb-2">Pre-Order Policies</h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Payment will be processed after order confirmation</li>
                  <li>• Estimated delivery: 2-4 weeks after payment</li>
                  <li>• Full refund available before shipping</li>
                  <li>• Returns accepted within 30 days of delivery</li>
                  <li>• Contact silentbye2@gmail.com for any questions</li>
                </ul>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className="w-full h-14 text-lg btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Submit Pre-Order
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              <Shield className="w-3 h-3 inline mr-1" />
              Secure checkout powered by Stripe. Your information is encrypted.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
